import { useState, useCallback } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Upload,
  FileText,
  Sparkles,
  Trash2,
  Archive,
  Inbox,
  Clock,
  Eye,
  Download,
  MoreVertical,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { AnalysisReviewModal } from "@/components/AnalysisReviewModal";
import { CSVPreview } from "@/components/CSVPreview";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import DashboardLayout from "@/components/DashboardLayout";
import { useIsMobile } from "@/hooks/useMobile";
import { useCompany } from "@/contexts/CompanyContext";
import {
  MobileTableCard,
  MobileTableCardHeader,
  MobileTableCardRow,
  MobileTableCardActions,
} from "@/components/MobileTableCard";

type FolderType = "inbox" | "processing" | "archived" | "deleted";

export default function Documents() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { selectedCompanyId } = useCompany();
  const utils = trpc.useUtils();
  const [activeFolder, setActiveFolder] = useState<FolderType>("inbox");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedDocForAnalysis, setSelectedDocForAnalysis] = useState<
    number | null
  >(null);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [csvPreviewDoc, setCsvPreviewDoc] = useState<{
    id: number;
    url: string;
    filename: string;
  } | null>(null);
  const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);

  // Queries
  const {
    data: documents,
    isLoading,
    refetch,
  } = trpc.documentManagement.listByFolder.useQuery(
    {
      companyId: selectedCompanyId,
      folder: activeFolder,
      limit: 50,
      offset: 0,
    },
    { enabled: !!user && !!selectedCompanyId }
  );

  const { data: folderStats } = trpc.documentManagement.getFolderStats.useQuery(
    { companyId: selectedCompanyId },
    { enabled: !!user && !!selectedCompanyId }
  );

  const { data: analysisData } = trpc.documentManagement.getAnalysis.useQuery(
    { documentId: selectedDocForAnalysis! },
    { enabled: !!selectedDocForAnalysis }
  );

  // Mutations
  const createDocument = trpc.documentManagement.create.useMutation();
  const moveToFolder = trpc.documentManagement.moveToFolder.useMutation();
  const analyzeDocument = trpc.documentManagement.analyze.useMutation();
  const deletePermanently =
    trpc.documentManagement.deletePermanently.useMutation();

  // Mutations for creating vendors and transactions from document analysis
  const createVendor = trpc.vendorIntelligence.upsertVendor.useMutation({
    onSuccess: () => {
      utils.vendorIntelligence.listVendors.invalidate();
      toast.success("Vendor created successfully");
    },
    onError: error => {
      toast.error(`Failed to create vendor: ${error.message}`);
    },
  });

  const createTransaction = trpc.transaction.create.useMutation({
    onSuccess: () => {
      utils.transaction.list.invalidate();
      toast.success("Transaction created successfully");
    },
    onError: error => {
      toast.error(`Failed to create transaction: ${error.message}`);
    },
  });

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  // Handle drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSelectedFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  // Upload files to S3 and create document records
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    try {
      for (const file of selectedFiles) {
        // Read file as base64
        const reader = new FileReader();
        const fileData = await new Promise<string>(resolve => {
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });

        // Create document record (backend will handle S3 upload)
        if (!selectedCompanyId) {
          toast.error("No company selected");
          continue;
        }

        await createDocument.mutateAsync({
          companyId: selectedCompanyId,
          filename: file.name,
          fileSize: file.size,
          mimeType: file.type,
          folder: "inbox",
          fileData, // Base64 data
        });
      }

      toast.success(`Uploaded ${selectedFiles.length} file(s) to Inbox`);
      setSelectedFiles([]);
      refetch();
    } catch (error) {
      toast.error("Failed to upload files");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  // Move document to different folder
  const handleMoveToFolder = async (
    documentId: number,
    targetFolder: FolderType
  ) => {
    if (!selectedCompanyId) {
      toast.error("Please select a company first");
      return;
    }
    try {
      await moveToFolder.mutateAsync({
        documentId,
        companyId: selectedCompanyId,
        targetFolder,
      });
      toast.success(`Moved to ${targetFolder}`);
      refetch();
    } catch (error) {
      toast.error("Failed to move document");
    }
  };

  // Analyze document with LLM
  const handleAnalyze = async (documentId: number) => {
    if (!selectedCompanyId) {
      toast.error("Please select a company first");
      return;
    }
    try {
      await analyzeDocument.mutateAsync({
        documentId,
        companyId: selectedCompanyId,
      });
      toast.success("Document analyzed successfully");
      refetch();
    } catch (error) {
      toast.error("Failed to analyze document");
    }
  };

  // Delete document permanently
  const handleDelete = async (documentId: number) => {
    if (!selectedCompanyId) {
      toast.error("Please select a company first");
      return;
    }
    try {
      await deletePermanently.mutateAsync({
        documentId,
        companyId: selectedCompanyId,
      });
      toast.success("Document deleted permanently");
      refetch();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const getFolderIcon = (folder: FolderType) => {
    switch (folder) {
      case "inbox":
        return <Inbox className="h-4 w-4" />;
      case "processing":
        return <Clock className="h-4 w-4" />;
      case "archived":
        return <Archive className="h-4 w-4" />;
      case "deleted":
        return <Trash2 className="h-4 w-4" />;
    }
  };

  const getDocumentTypeColor = (type?: string) => {
    switch (type) {
      case "invoice":
        return "bg-primary";
      case "receipt":
        return "bg-success";
      case "contract":
        return "bg-accent";
      case "bank_statement":
        return "bg-warning";
      case "tax_form":
        return "bg-destructive";
      default:
        return "bg-muted-foreground";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6 pb-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            Document Management
          </h1>
          <p className="text-muted-foreground text-sm md:text-base">
            Upload, organize, and analyze your business documents with
            AI-powered extraction
          </p>
        </div>

        {/* Upload Area */}
        <Card className="p-4 md:p-6">
          <div
            className={`border-2 border-dashed rounded-lg p-6 md:p-8 text-center transition-colors ${
              dragActive ? "border-primary bg-primary/5" : "border-border"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-base md:text-lg font-semibold mb-2">
              Drag and drop files here, or click to browse
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground mb-4">
              Supported formats: PDF, images, Word documents, Excel
              spreadsheets, CSV files
            </p>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv"
            />
            <label htmlFor="file-upload">
              <Button
                variant="outline"
                asChild
                className="min-h-[44px] md:min-h-[36px]"
              >
                <span>Browse Files</span>
              </Button>
            </label>

            {selectedFiles.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">
                  {selectedFiles.length} file(s) selected:
                </p>
                <ul className="text-xs md:text-sm text-muted-foreground space-y-1 max-h-32 overflow-y-auto">
                  {selectedFiles.map((file, i) => (
                    <li key={i}>
                      {file.name} ({formatFileSize(file.size)})
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="mt-4 min-h-[44px]"
                >
                  {uploading ? "Uploading..." : "Upload to Inbox"}
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Folder Tabs */}
        <Tabs
          value={activeFolder}
          onValueChange={v => setActiveFolder(v as FolderType)}
        >
          <TabsList
            className={`grid w-full ${isMobile ? "grid-cols-2 gap-2" : "grid-cols-4"}`}
          >
            <TabsTrigger
              value="inbox"
              className="flex items-center gap-1 md:gap-2 text-xs md:text-sm min-h-[44px] md:min-h-[36px]"
            >
              <Inbox className="h-3 w-3 md:h-4 md:w-4" />
              {!isMobile && "Inbox"}
              {folderStats?.inbox ? ` (${folderStats.inbox})` : ""}
            </TabsTrigger>
            <TabsTrigger
              value="processing"
              className="flex items-center gap-1 md:gap-2 text-xs md:text-sm min-h-[44px] md:min-h-[36px]"
            >
              <Clock className="h-3 w-3 md:h-4 md:w-4" />
              {!isMobile && "Processing"}
              {folderStats?.processing ? ` (${folderStats.processing})` : ""}
            </TabsTrigger>
            <TabsTrigger
              value="archived"
              className="flex items-center gap-1 md:gap-2 text-xs md:text-sm min-h-[44px] md:min-h-[36px]"
            >
              <Archive className="h-3 w-3 md:h-4 md:w-4" />
              {!isMobile && "Archived"}
              {folderStats?.archived ? ` (${folderStats.archived})` : ""}
            </TabsTrigger>
            <TabsTrigger
              value="deleted"
              className="flex items-center gap-1 md:gap-2 text-xs md:text-sm min-h-[44px] md:min-h-[36px]"
            >
              <Trash2 className="h-3 w-3 md:h-4 md:w-4" />
              {!isMobile && "Deleted"}
              {folderStats?.deleted ? ` (${folderStats.deleted})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeFolder} className="mt-4 md:mt-6">
            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">
                Loading documents...
              </div>
            ) : !documents || documents.length === 0 ? (
              <Card className="p-8 md:p-12 text-center">
                <FileText className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-base md:text-lg font-semibold mb-2">
                  No documents in {activeFolder}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {activeFolder === "inbox"
                    ? "Upload files to get started"
                    : `Move documents to ${activeFolder} to see them here`}
                </p>
              </Card>
            ) : (
              <div className="space-y-3 md:space-y-4">
                {documents.map(doc => (
                  <Card key={doc.id} className="p-4 md:p-6">
                    {/* Header: Original filename + Quick detection badge */}
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          <h3 className="font-semibold text-base md:text-lg truncate">
                            {doc.originalName || doc.fileName}
                          </h3>
                        </div>
                        {doc.originalName &&
                          doc.originalName !== doc.fileName && (
                            <p className="text-xs text-muted-foreground pl-7">
                              Stored as: {doc.fileName}
                            </p>
                          )}
                      </div>

                      {/* Smart detection badge */}
                      {doc.documentType && (
                        <Badge
                          variant="secondary"
                          className={`${getDocumentTypeColor(doc.documentType)} text-white flex-shrink-0`}
                        >
                          {doc.documentType.replace("_", " ")}
                        </Badge>
                      )}
                    </div>

                    {/* File metadata in clean grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs">Type</p>
                        <p className="font-medium">
                          {doc.mimeType.split("/")[1]?.toUpperCase() ||
                            "Unknown"}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Size</p>
                        <p className="font-medium">
                          {formatFileSize(doc.fileSize)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">
                          Uploaded
                        </p>
                        <p className="font-medium">
                          {new Date(doc.uploadedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Status</p>
                        <p className="font-medium flex items-center gap-1">
                          {doc.status === "error" ? (
                            <>
                              <AlertCircle className="h-3 w-3 text-destructive" />
                              <span className="text-destructive">Error</span>
                            </>
                          ) : doc.status === "processing" ? (
                            <>
                              <Clock className="h-3 w-3 text-warning" />
                              <span className="text-warning">Processing</span>
                            </>
                          ) : doc.analyzedAt ? (
                            <>
                              <CheckCircle2 className="h-3 w-3 text-success" />
                              Analyzed
                            </>
                          ) : (
                            <>
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              Pending
                            </>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Error message display */}
                    {doc.status === "error" && doc.errorMessage && (
                      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-destructive mb-1">
                              Analysis Failed
                            </p>
                            <p className="text-xs text-destructive/80">
                              {doc.errorMessage}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Extracted data preview */}
                    {(doc.extractedVendorName || doc.extractedAmount) && (
                      <div className="bg-muted/50 rounded-lg p-3 mb-4 space-y-1">
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          Extracted Data
                        </p>
                        {doc.extractedVendorName && (
                          <p className="text-sm">
                            <span className="text-muted-foreground">
                              Vendor:
                            </span>{" "}
                            <span className="font-medium">
                              {doc.extractedVendorName}
                            </span>
                          </p>
                        )}
                        {doc.extractedAmount && (
                          <p className="text-sm">
                            <span className="text-muted-foreground">
                              Amount:
                            </span>{" "}
                            <span className="font-medium">
                              CHF {doc.extractedAmount}
                            </span>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Primary actions - prominent buttons */}
                    <div className="flex flex-wrap gap-2">
                      {doc.status === "error" ? (
                        <Button
                          onClick={() => handleAnalyze(doc.id)}
                          variant="default"
                          className="flex-1 sm:flex-none min-h-[44px]"
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          Retry Analysis
                        </Button>
                      ) : !doc.analyzedAt ? (
                        <Button
                          onClick={() => handleAnalyze(doc.id)}
                          className="flex-1 sm:flex-none min-h-[44px]"
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          Analyze with AI
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            setSelectedDocForAnalysis(doc.id);
                            setAnalysisModalOpen(true);
                          }}
                          variant="default"
                          className="flex-1 sm:flex-none min-h-[44px]"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Analysis
                        </Button>
                      )}

                      {doc.mimeType === "text/csv" && (
                        <Button
                          onClick={() => {
                            setCsvPreviewDoc({
                              id: doc.id,
                              url: doc.fileUrl,
                              filename: doc.fileName,
                            });
                            setCsvPreviewOpen(true);
                          }}
                          variant="outline"
                          className="flex-1 sm:flex-none min-h-[44px]"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Preview CSV
                        </Button>
                      )}

                      {/* Secondary actions dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="min-h-[44px]"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </DropdownMenuItem>
                          {activeFolder !== "processing" && (
                            <DropdownMenuItem
                              onClick={() =>
                                handleMoveToFolder(doc.id, "processing")
                              }
                            >
                              <Clock className="h-4 w-4 mr-2" />
                              Move to Processing
                            </DropdownMenuItem>
                          )}
                          {activeFolder !== "archived" && (
                            <DropdownMenuItem
                              onClick={() =>
                                handleMoveToFolder(doc.id, "archived")
                              }
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Move to Archived
                            </DropdownMenuItem>
                          )}
                          {activeFolder !== "deleted" && (
                            <DropdownMenuItem
                              onClick={() =>
                                handleMoveToFolder(doc.id, "deleted")
                              }
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Move to Deleted
                            </DropdownMenuItem>
                          )}
                          {activeFolder === "deleted" && (
                            <DropdownMenuItem
                              onClick={() => handleDelete(doc.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Analysis Review Modal */}
        <AnalysisReviewModal
          open={analysisModalOpen}
          onOpenChange={setAnalysisModalOpen}
          documentName={
            documents?.find(d => d.id === selectedDocForAnalysis)?.fileName ||
            ""
          }
          analysis={
            analysisData
              ? {
                  documentType:
                    documents?.find(d => d.id === selectedDocForAnalysis)
                      ?.documentType || "unknown",
                  confidence: analysisData.overallConfidence,
                  vendorCandidates: analysisData.vendorCandidates,
                  transactionCandidates: analysisData.transactionCandidates,
                }
              : null
          }
          onCreateVendor={vendor => {
            if (!selectedCompanyId) {
              toast.error("Please select a company first");
              return;
            }
            createVendor.mutate({
              companyId: selectedCompanyId,
              name: vendor.name || "Unknown Vendor",
              category: vendor.category,
              vendorType: vendor.vendorType as any,
              swissVendor: vendor.swissVendor,
              uid: vendor.uid,
              vatNumber: vendor.vatNumber,
              address: vendor.address,
            });
          }}
          onCreateTransaction={transaction => {
            if (!selectedCompanyId) {
              toast.error("Please select a company first");
              return;
            }
            createTransaction.mutate({
              companyId: selectedCompanyId,
              date: transaction.date || Date.now(),
              description:
                transaction.description || "Transaction from document",
              amount: Math.round((transaction.amount || 0) * 100), // Convert to cents
              currency: transaction.currency || "CHF",
              type: transaction.type || "debit",
              category: transaction.category || "Operating Expenses",
              accountCode: transaction.accountCode || "6000",
              accountName: transaction.accountName || "Operating Expenses",
              vatRate: transaction.vatRate,
              vatAmount: transaction.vatAmount
                ? Math.round(transaction.vatAmount * 100)
                : undefined,
              reference: transaction.reference,
              notes: transaction.notes,
            });
          }}
        />

        {/* CSV Preview Dialog */}
        <Dialog open={csvPreviewOpen} onOpenChange={setCsvPreviewOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>CSV Preview</DialogTitle>
            </DialogHeader>
            {csvPreviewDoc && (
              <CSVPreview
                url={csvPreviewDoc.url}
                fileName={csvPreviewDoc.filename}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
